// ============================================
// AVALIAÇÃO DO DOADOR → IA — whitelist + derivados (B351 passo 5b, AI_153)
// ============================================
// A aba "Avaliação do Doador" grava em `paciente.avaliacaoDoador`, que é
// `Record<string, any>` — sem proteção de tipo. Despejar o objeto inteiro no
// prompt levaria `receptorId` e `receptorNome` (IDENTIFICADORES DE TERCEIRO)
// junto, e nenhuma rede a jusante os pegaria: `validarAnonimizacao` só confere
// campos do PRÓPRIO paciente e `redigirPiiEmObjeto` não toca nomes.
//
// Por isso a passagem é por WHITELIST explícita, campo a campo — mesmo padrão já
// validado ao vivo no prompt de imagem do doador (`gerarPromptExameImagem`).
// Campo novo na aba NÃO flui sozinho: é para ser assim.
//
// ⚠️ Todos os campos da aba são gravados como STRING (inclusive os `type=number`
// — `AbaAvaliacaoDoador` faz `set(campo, e.target.value)`). Aqui os numéricos
// passam por `numero()`, que trata vazio/NaN como AUSENTE — nunca como zero.
//
// Os DERIVADOS (GRWR, remanescente, IMC) vêm da fonte única `volumetriaDoador`,
// calculados pelo SISTEMA e entregues prontos: a IA confere, não recalcula. É a
// lição 4 do passo 5a (o peso do receptor já estava digitado e o prompt não o
// entregava) resolvida na origem.

import {
  calcularGRWR,
  calcularRemanescente,
  calcularIMC,
  classificarGRWR,
  classificarRemanescente,
  Classificacao,
} from "../../../shared/utils/volumetriaDoador";

/** Campos de TEXTO LIVRE da aba — passam por redação de PII antes do prompt. */
export const CAMPOS_TEXTO_LIVRE_DOADOR = [
  "comorbidades",
  "anatomiaDetalhe",
  "psicossocialObs",
  "observacoes",
] as const;

/**
 * Campos que a aba grava e que PODEM ir à IA.
 *
 * ⛔ FORA por construção: `receptorId` e `receptorNome`. O vínculo com o
 * receptor entra pelo conjunto mínimo (montarConjuntoMinimoReceptor), não por
 * aqui — e o receptor nunca é nomeado.
 */
export const CAMPOS_DOADOR_PARA_IA = [
  // Identificação clínica do doador
  "peso",
  "altura",
  "tipoSanguineo",
  "comorbidades",
  // Trombofilia (os 8 exames da aba)
  "fatorVLeiden",
  "protrombinaG20210A",
  "proteinaC",
  "proteinaS",
  "antitrombina3",
  "anticardiolipina",
  "antiB2Glicoproteina",
  "anticoagulanteLupico",
  // Volumetria e enxerto
  "loboDoado",
  "volumeFigadoTotal",
  "volumeEnxerto",
  "pesoReceptor",
  // Chaves LEGADAS (antes do B351-FU, quando o campo era em gramas). Lidas como
  // reserva para não perder o que já foi digitado; ver AbaAvaliacaoDoador.
  "pesoFigadoTotal",
  "pesoEnxerto",
  // Anatomia
  "variacaoArterial",
  "variacaoPortal",
  "variacaoBiliar",
  "veiasHepaticas",
  "tributariaV5",
  "tributariaV8",
  "incluiVeiaHepaticaMedia",
  "anatomiaDetalhe",
  // Esteatose
  "esteatosePercentual",
  "esteatoseMetodo",
  // Estágio: a doação já aconteceu? (B351-FU8) — muda a PERGUNTA da análise.
  "doacaoRealizada",
  "dataDoacao",
  // Psicossocial e moldura legal
  "psicossocialParecer",
  "psicossocialData",
  "psicossocialObs",
  "parentesco",
  "autorizacaoJudicial",
  "autorizacaoJudicialData",
  // Observações gerais
  "observacoes",
] as const;

/** Chaves que NUNCA podem atravessar (identificadores do receptor). */
export const CAMPOS_DOADOR_PROIBIDOS = ["receptorId", "receptorNome"] as const;

export interface DerivadosDoador {
  /** Enxerto ÷ peso do receptor, em % (1 cm³ ≈ 1 g). null se faltar insumo. */
  grwr: number | null;
  grwrClassificacao: Classificacao;
  /** (total − enxerto) ÷ total, em %. null se faltar insumo ou for incoerente. */
  remanescente: number | null;
  remanescenteClassificacao: Classificacao;
  /** IMC do doador (kg/m²). */
  imc: number | null;
}

export interface AvaliacaoDoadorAnonimizada {
  /** Campos da aba que passaram pela whitelist (só os preenchidos). */
  campos: Record<string, string>;
  /** Números que o SISTEMA calculou — a IA confere, não recalcula. */
  derivados: DerivadosDoador;
}

/** Converte string da aba em número; vazio/NaN = ausente (nunca zero). */
function numero(v: unknown): number | null {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/**
 * Aplica a whitelist e calcula os derivados.
 *
 * @param avaliacaoDoador  `paciente.avaliacaoDoador` cru (pode ser undefined).
 * @returns  null quando não há NADA a informar (aba vazia) — assim o formatador
 *           não imprime um cabeçalho oco, que a IA leria como "avaliado e sem
 *           achados" (ausência de evidência virando tranquilidade, lição 6).
 */
export function montarAvaliacaoDoadorParaIA(
  avaliacaoDoador: Record<string, any> | undefined | null
): AvaliacaoDoadorAnonimizada | null {
  const origem = avaliacaoDoador || {};
  const campos: Record<string, string> = {};

  for (const chave of CAMPOS_DOADOR_PARA_IA) {
    const valor = origem[chave];
    if (valor === undefined || valor === null) continue;
    const texto = String(valor).trim();
    if (texto === "") continue;
    campos[chave] = texto;
  }

  // Volumetria em cm³ (chave nova) com as chaves em gramas como reserva.
  const volTotal = numero(origem.volumeFigadoTotal ?? origem.pesoFigadoTotal);
  const volEnxerto = numero(origem.volumeEnxerto ?? origem.pesoEnxerto);
  const pesoReceptor = numero(origem.pesoReceptor);
  const grwr = calcularGRWR(volEnxerto, pesoReceptor);
  const remanescente = calcularRemanescente(volTotal, volEnxerto);
  const imc = calcularIMC(numero(origem.peso), numero(origem.altura));

  const derivados: DerivadosDoador = {
    grwr,
    grwrClassificacao: classificarGRWR(grwr),
    remanescente,
    remanescenteClassificacao: classificarRemanescente(remanescente),
    imc,
  };

  const temDerivado = grwr !== null || remanescente !== null || imc !== null;
  if (Object.keys(campos).length === 0 && !temDerivado) return null;

  return { campos, derivados };
}
